import React from 'react';
import { useRouter } from 'next/router';
import { useSetAtom } from 'jotai';
import { useUser } from '@clerk/nextjs';
import { LoginType } from 'auth/constants';
import { useUserDetails } from 'auth/mutations/userHooks';
import { useUserDataMutation } from 'components/auth/hooks/authFlowHooks';
import useAuthComplete from 'components/auth/hooks/useAuthComplete';
import { useFeatureFlagQueryByName } from 'components/layouts/hooks';
import useLazySnackbar from 'components/banners/useLazySnackbar';
import { incompleteUserDataAtom } from 'components/auth/store';
import { accountCompletedAtom } from 'components/checkout/buoy/jotaiStore';
import { logoutRedirectUrlAtom } from 'auth/userStore';
import { getUserMetadata, isAccountCompleted } from 'helpers/clerk';
import boatsetterPaths, { boatsetterFlags } from 'utils/boatsetterPaths';

type AppRequest = { mode: 'app'; redirectTo: string };
type AuthRequest = { mode: 'auth'; loginType: LoginType; isFirstTimeSocial: boolean };
type IncompleteReq = AppRequest | AuthRequest | null;

export default function useIncompleteUserProcessing() {
  const router = useRouter();
  const { enqueueSnackbar } = useLazySnackbar();
  const { handleUserAfterAuthActions, runAnalytics } = useAuthComplete();

  const { user, isLoaded: isClerkLoaded } = useUser();
  const { userDetails, isLoading: useUserDetailsIsLoading } = useUserDetails();
  const userDataMutation = useUserDataMutation();

  const { flag: isClerkEnabled, isLoading: isFeatureFlagLoading } = useFeatureFlagQueryByName(
    boatsetterFlags.clerkSignup
  );

  const setIncompleteUserData = useSetAtom(incompleteUserDataAtom);
  const setAccountCompleted = useSetAtom(accountCompletedAtom);
  const setLogoutRedirectUrl = useSetAtom(logoutRedirectUrlAtom);

  const [request, setRequest] = React.useState<IncompleteReq>(null);
  const pendingResolve = React.useRef<(value: boolean) => void>();

  const authSource = React.useMemo<'unknown' | 'clerk' | 'legacy'>(() => {
    if (isFeatureFlagLoading) return 'unknown';
    return isClerkEnabled ? 'clerk' : 'legacy';
  }, [isFeatureFlagLoading, isClerkEnabled]);

  const isReady = React.useMemo(() => {
    if (authSource === 'unknown') return false;
    if (authSource === 'clerk') return isClerkLoaded && !!user;
    return !useUserDetailsIsLoading && !!userDetails;
  }, [authSource, isClerkLoaded, user, useUserDetailsIsLoading, userDetails]);

  const resolve = (value: boolean) => {
    pendingResolve.current?.(value);
    pendingResolve.current = undefined;
  };

  const handleClerkAuthFlow = async (meta, currentRequest) => {
    const accountCompleted = isAccountCompleted(user, meta);
    const { loginType, isFirstTimeSocial } = currentRequest;

    runAnalytics(
      {
        id: user?.id,
        email: user?.primaryEmailAddress?.emailAddress || '',
        created_at: new Date(user?.createdAt).toISOString(),
      },
      loginType
    );

    if (accountCompleted) {
      await handleUserAfterAuthActions();
      setAccountCompleted(true);
      resolve(true);
      return;
    }

    setIncompleteUserData({
      phone: user?.primaryPhoneNumber?.phoneNumber || '',
      last_name: user?.lastName || '',
      first_name: user?.firstName || '',
      birthdate: meta?.birthdate || '',
      email: user?.primaryEmailAddress?.emailAddress || '',
      account_completed: false,
      tcpa_accepted_at: meta?.tcpa_accepted_at || '',
      accepted_terms_at: meta?.accepted_terms_at || '',
      phone_verified: user?.primaryPhoneNumber?.verification?.status === 'verified' || false,
    });

    await router.push({
      pathname: `${boatsetterPaths.signUp}/clerk_complete_account`,
      query: { ...router.query, isFirstTimeSocial },
    });

    resolve(false);
  };

  const handleClerkAppFlow = async (meta, redirectTo: string) => {
    const accountCompleted = isAccountCompleted(user, meta);

    if (accountCompleted) {
      setAccountCompleted(true);
      resolve(true);
      return;
    }

    setIncompleteUserData({
      phone: user?.primaryPhoneNumber?.phoneNumber || '',
      last_name: user?.lastName || '',
      first_name: user?.firstName || '',
      birthdate: meta.birthdate || '',
      email: user?.primaryEmailAddress?.emailAddress || '',
      account_completed: false,
      tcpa_accepted_at: meta?.tcpa_accepted_at || '',
      accepted_terms_at: meta?.accepted_terms_at || '',
      phone_verified: user?.primaryPhoneNumber?.verification?.status === 'verified' || false,
    });

    await router.push({
      pathname: boatsetterPaths.auth.completeAccount,
      query: { redirect_to: redirectTo },
    });

    setAccountCompleted(false);
    resolve(false);
  };

  const handleLegacyAuthFlow = async (legacyData, currentRequest) => {
    const { loginType, isFirstTimeSocial } = currentRequest;

    runAnalytics(legacyData, loginType);

    setIncompleteUserData({
      phone: legacyData.phone || '',
      last_name: legacyData.last_name || '',
      first_name: legacyData.first_name || '',
      birthdate: legacyData.birthdate || '',
      email: legacyData.email || '',
      account_completed: legacyData.account_completed,
      tcpa_accepted_at: legacyData.tcpa_accepted_at || '',
      accepted_terms_at: legacyData.accepted_terms_at || '',
      phone_verified: legacyData.phone_verified || false,
    });

    if (legacyData.account_completed) {
      await handleUserAfterAuthActions();
      setAccountCompleted(true);
      resolve(true);
      return;
    }

    await router.push({
      pathname: `${boatsetterPaths.signUp}/complete_account`,
      query: { ...router.query, isFirstTimeSocial },
    });

    resolve(false);
  };

  const handleLegacyAppFlow = async (legacyData, redirectTo: string) => {
    if (legacyData.account_completed) {
      setAccountCompleted(true);
      resolve(true);
      return;
    }

    setIncompleteUserData({
      phone: legacyData.phone || '',
      last_name: legacyData.last_name || '',
      first_name: legacyData.first_name || '',
      birthdate: legacyData.birthdate || '',
      email: legacyData.email || '',
      account_completed: legacyData.account_completed,
      tcpa_accepted_at: legacyData.tcpa_accepted_at || '',
      accepted_terms_at: legacyData.accepted_terms_at || '',
      phone_verified: legacyData.phone_verified || false,
    });

    await router.push({
      pathname: `${boatsetterPaths.signUp}/complete_account`,
      query: { redirect_to: redirectTo },
    });

    setAccountCompleted(false);
    resolve(false);
  };

  React.useEffect(() => {
    if (!isReady || !request) return;
    const currentRequest = request;
    setRequest(null);

    const run = async () => {
      try {
        if (authSource === 'clerk') {
          const meta = getUserMetadata(user!.unsafeMetadata as Record<string, unknown>);
          if (currentRequest.mode === 'auth') {
            await handleClerkAuthFlow(meta, currentRequest);
          } else {
            await handleClerkAppFlow(meta, currentRequest.redirectTo);
          }
          return;
        }

        const legacyData = currentRequest.mode === 'auth' ? await userDataMutation.mutateAsync() : userDetails!;
        if (currentRequest.mode === 'auth') {
          await handleLegacyAuthFlow(legacyData, currentRequest);
        } else {
          await handleLegacyAppFlow(legacyData, currentRequest.redirectTo);
        }
      } catch (err) {
        console.error('Account-completion flow failed', err);
        enqueueSnackbar('Something went wrong. Please log in again.', { variant: 'error' });
        setLogoutRedirectUrl(`/a/auth?redirect_to=${router.asPath}`);
        await router.push(boatsetterPaths.logOut);
        resolve(false);
      }
    };

    run();
  }, [isReady, request, authSource, user, userDetails, router, userDataMutation]);

  const handlePossibleIncompleteAccountInApp = (redirectTo: string): Promise<boolean> => {
    if (authSource === 'clerk' && isClerkLoaded && !user) {
      router.push(boatsetterPaths.logOut);
      return Promise.resolve(false);
    }

    return new Promise<boolean>((resolveFn) => {
      pendingResolve.current = resolveFn;
      setRequest({ mode: 'app', redirectTo });
    });
  };

  const handlePossiblyIncompleteAccountInAuth = (
    loginType: LoginType,
    isFirstTimeSocial: boolean
  ): Promise<boolean> => {
    if (authSource === 'clerk' && isClerkLoaded && !user) {
      router.push(boatsetterPaths.logOut);
      return Promise.resolve(false);
    }

    return new Promise<boolean>((resolveFn) => {
      pendingResolve.current = resolveFn;
      setRequest({ mode: 'auth', loginType, isFirstTimeSocial });
    });
  };

  return {
    handlePossibleIncompleteAccountInApp,
    handlePossiblyIncompleteAccountInAuth,
    isReady,
  };
}
